import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  Lock, Trophy, Gem, Users, Crown, Gift, CheckCircle2,
  Loader2, MessageSquare, Link2,
} from 'lucide-react';

// =============================================================================
// /spadeslaunch — Founding Player conversion funnel for in-person QR traffic.
// Production-ready, mobile-first, single component. Writes signups to the
// existing `db.waitlist` collection (same as the homepage form) via the
// existing POST /api/waitlist/signup endpoint with source="Spadeslaunch_page".
// =============================================================================

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Self-hosted via /api/assets/spadeslaunch — the customer-assets CDN host
// got blocked by browser-level extensions for some QR-scan users on May 23,
// producing broken <img> placeholders. Serving from the same origin as the
// API (a) eliminates CORS / extension blocking, (b) survives without
// depending on the external CDN, (c) loads on the first request from the
// gemslive.tv preview cache.
const ASSETS = {
  logo: `${API}/assets/spadeslaunch/gems_logo.png`,
  gameplay: `${API}/assets/spadeslaunch/gems_spades_gameplay.jpg`,
  flyer: `${API}/assets/spadeslaunch/gems_spadeslaunch_flyer.png`,
  // May 23 update — QR + printable-flyer downloads exposed in the signup
  // section so visitors can scan with a phone OR grab the printable assets.
  flyerPrintable: `${API}/assets/spadeslaunch/gems_flyer_with_qr.png`,
  qrSolid: `${API}/assets/spadeslaunch/gems_spadeslaunch_qr_solid.png`,  // black-on-white — most-scannable on screens
  qrSvg: `${API}/assets/spadeslaunch/gems_spadeslaunch_qr.svg`,
};

const HEARD_OPTIONS = [
  'Local Spades Tournament',
  'Friend / Word of Mouth',
  'Facebook Group',
  'Instagram',
  'TikTok',
  'YouTube',
  'Other',
];

// Analytics helper — currently console.log; swap for real provider later.
const track = (event, payload = {}) => {
  try {
    // eslint-disable-next-line no-console
    console.log('[analytics]', event, payload);
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', event, payload);
    }
  } catch { /* never throw from analytics */ }
};

const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e || '');
const isValidPhone = (p) => {
  if (!p) return false;
  const digits = p.replace(/[^0-9]/g, '');
  return digits.length >= 10 && digits.length <= 15;
};

// =============================================================================
// Page component
// =============================================================================
export default function SpadeslaunchPage() {
  const formRef = useRef(null);
  const [formStarted, setFormStarted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [confirmation, setConfirmation] = useState({ email: '', already: false });
  const [stickyVisible, setStickyVisible] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);

  const [form, setForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    city_state: '',
    signup_source_detail: '',
  });
  const [errors, setErrors] = useState({});

  // ---------------------------------------------------------------------------
  // Page-load housekeeping: tab title, meta, page-view event, qr param, counter
  // ---------------------------------------------------------------------------
  const qrSource = (() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get('qr') === '1';
    } catch { return false; }
  })();

  useEffect(() => {
    document.title = 'GEMS Live — Become a Founding Player';
    // Meta description (create if missing).
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'description';
      document.head.appendChild(meta);
    }
    const prevContent = meta.getAttribute('content');
    meta.setAttribute(
      'content',
      'The real online home for Spades is coming. NY-style rules, live face-to-face gameplay, real competition. Claim your founding player spot.'
    );
    track('spadeslaunch_page_view', { qr_source: qrSource });
    return () => {
      if (prevContent !== null) meta.setAttribute('content', prevContent);
    };
  }, [qrSource]);

  // Live counter fetch removed (May 14 update): replaced with a non-numeric
  // "Founding Player Access" badge to avoid showing low signup counts during
  // initial launch / waitlist warm-up. The /api/waitlist/spadeslaunch/count
  // endpoint is left in place for future use / internal analytics.

  // Sticky CTA: visible after scrolling past hero, hidden when form in viewport.
  useEffect(() => {
    const onScroll = () => {
      if (!formRef.current) return;
      const rect = formRef.current.getBoundingClientRect();
      const pastHero = window.scrollY > 360;
      const formInView = rect.top < window.innerHeight && rect.bottom > 0;
      setStickyVisible(pastHero && !formInView && !submitted);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [submitted]);

  // ---------------------------------------------------------------------------
  // Form interaction
  // ---------------------------------------------------------------------------
  const scrollToForm = useCallback((origin) => {
    track('spadeslaunch_cta_clicked', { origin, qr_source: qrSource });
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [qrSource]);

  const handleFocus = () => {
    if (!formStarted) {
      setFormStarted(true);
      track('spadeslaunch_form_started', { qr_source: qrSource });
    }
  };

  const handleChange = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
    if (errors[field]) setErrors((e2) => ({ ...e2, [field]: null }));
  };

  const validate = () => {
    const next = {};
    if (!form.full_name || form.full_name.trim().length < 2) next.full_name = 'Please enter your name';
    if (!isValidEmail(form.email)) next.email = 'Please enter a valid email';
    if (!isValidPhone(form.phone)) next.phone = 'Please enter a valid phone number';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const isFormValid = (
    form.full_name.trim().length >= 2 &&
    isValidEmail(form.email) &&
    isValidPhone(form.phone)
  );

  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (submitting) return;
    if (!validate()) {
      track('spadeslaunch_form_error', { qr_source: qrSource });
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        email: form.email.trim().toLowerCase(),
        source: 'Spadeslaunch_page',
        full_name: form.full_name.trim(),
        phone: form.phone.trim(),
        city_state: form.city_state.trim() || undefined,
        signup_source_detail: form.signup_source_detail || undefined,
        qr_source: qrSource,
      };
      const res = await axios.post(`${API}/waitlist/signup`, payload, { timeout: 15000 });
      const already = !!res?.data?.already_registered;
      if (already) track('spadeslaunch_duplicate_email', { qr_source: qrSource });
      track('spadeslaunch_form_submitted', {
        qr_source: qrSource,
        already_registered: already,
        signup_source_detail: form.signup_source_detail || '',
      });
      setConfirmation({ email: payload.email, already });
      setSubmitted(true);
      // Auto-scroll the confirmation into view.
      setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Something went wrong. Please try again.';
      setErrors((e2) => ({ ...e2, _form: msg }));
      track('spadeslaunch_form_error', { qr_source: qrSource, message: msg });
    } finally {
      setSubmitting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Share helpers (confirmation state)
  // ---------------------------------------------------------------------------
  const shareUrl = (typeof window !== 'undefined')
    ? `${window.location.origin}/spadeslaunch`
    : 'https://gemslive.tv/spadeslaunch';
  const shareText = "I just claimed my Founding Player spot on GEMS Live — the real online home for Spades. Get yours:";

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2200);
    } catch { /* clipboard blocked — ignore */ }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div
      className="min-h-screen w-full text-white"
      style={{ background: '#0A0A0A', fontFamily: "'Manrope', 'Inter', system-ui, -apple-system, sans-serif" }}
      data-testid="spadeslaunch-page"
    >
      <SpadesGlobalStyles />

      {/* ============================================================ HERO */}
      <header
        className="relative overflow-hidden"
        style={{
          background:
            'radial-gradient(900px 600px at 10% -10%, rgba(0,201,106,0.18) 0%, rgba(0,201,106,0) 60%), #0A0A0A',
        }}
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-6 pb-12 sm:pt-10 sm:pb-20">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center gap-3" data-testid="spadeslaunch-logo">
              <img src={ASSETS.logo} alt="GEMS Live" className="h-9 sm:h-11 w-auto" loading="eager" />
              <span className="hidden sm:inline text-base font-bold tracking-wide">GEMS Live</span>
            </a>
            <span
              className="hidden sm:inline-block uppercase text-xs font-bold tracking-widest px-3 py-1.5 border-2 rounded-sm"
              style={{ borderColor: '#E63946', color: '#FFFFFF', transform: 'rotate(-3deg)' }}
            >
              Built by players · For players
            </span>
          </div>

          <div className="mt-10 sm:mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            {/* Hero text column */}
            <div className="lg:col-span-7">
              <h1 className="spades-hero-headline" data-testid="spadeslaunch-hero-headline">
                <span className="block text-white">THE REAL</span>
                <span className="block" style={{ color: '#00C96A' }}>ONLINE HOME</span>
                <span className="block" style={{ color: '#E63946' }}>FOR SPADES</span>
              </h1>

              <p className="mt-6 text-lg sm:text-xl font-medium text-white/90 max-w-xl">
                <span style={{ color: '#00C96A' }}>REAL</span> rules.{' '}
                <span style={{ color: '#00C96A' }}>REAL</span> players.{' '}
                <span style={{ color: '#E63946' }}>REAL</span> competition.
              </p>

              <span
                className="inline-block sm:hidden mt-5 uppercase text-[10px] font-bold tracking-widest px-3 py-1.5 border-2 rounded-sm"
                style={{ borderColor: '#E63946', color: '#FFFFFF', transform: 'rotate(-3deg)' }}
              >
                Built by players · For players
              </span>

              <div className="mt-8 grid grid-cols-1 gap-2 max-w-md">
                <Bullet label="NY-STYLE SPADES" />
                <Bullet label={<><span className="font-black" style={{ color: '#00C96A' }}>LIVE</span> FACE-TO-FACE</>} />
                <Bullet label={<><span className="font-black" style={{ color: '#E63946' }}>COMPETE.</span> STREAM.</>} />
                <Bullet label={<span className="font-black" style={{ color: '#E63946' }}>BELONG.</span>} />
              </div>

              <div className="mt-7 flex items-center gap-3 text-3xl select-none" aria-hidden="true">
                <span className="spades-suit spades-suit-1">♠</span>
                <span className="spades-suit spades-suit-2" style={{ color: '#E63946' }}>♥</span>
                <span className="spades-suit spades-suit-3" style={{ color: '#E63946' }}>♦</span>
                <span className="spades-suit spades-suit-4">♣</span>
              </div>

              <div className="mt-8">
                <button
                  type="button"
                  onClick={() => scrollToForm('hero')}
                  data-testid="hero-cta-button"
                  className="w-full sm:w-auto text-base sm:text-lg font-extrabold uppercase tracking-wide px-7 py-4 rounded-md transition-transform active:scale-95"
                  style={{
                    background: 'linear-gradient(180deg, #00DD75 0%, #00B85F 100%)',
                    color: '#0A0A0A',
                    boxShadow: '0 10px 30px -10px rgba(0,201,106,0.6), inset 0 -2px 0 rgba(0,0,0,0.2)',
                  }}
                >
                  Claim Your Founding Player Spot →
                </button>
              </div>
            </div>

            {/* Hero gameplay screenshot */}
            <div className="lg:col-span-5">
              <div
                className="relative rounded-xl overflow-hidden border"
                style={{ borderColor: '#2A2A2A', boxShadow: '0 30px 80px -20px rgba(0,201,106,0.25)' }}
              >
                <img
                  src={ASSETS.gameplay}
                  alt="Four real players streaming a live Spades match on GEMS Live — face-to-face split-screen with cards in play"
                  className="w-full h-auto block"
                  loading="eager"
                  fetchpriority="high"
                />
                <span
                  className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-[11px] font-bold uppercase tracking-wider"
                  style={{ background: '#E63946', color: '#FFFFFF' }}
                >
                  <span className="spades-pulse" /> Live
                </span>
              </div>

              {/* NY ruleset callout */}
              <div
                className="mt-5 rounded-lg p-4 sm:p-5 border-2"
                style={{ borderColor: '#00C96A', background: 'rgba(0,201,106,0.06)' }}
                data-testid="ny-ruleset-callout"
              >
                <div className="text-xs font-extrabold tracking-widest uppercase" style={{ color: '#00C96A' }}>
                  NY Ruleset
                </div>
                <ul className="mt-2 text-sm text-white/90 grid grid-cols-2 gap-x-4 gap-y-1">
                  <li>• Big Joker</li>
                  <li>• Little Joker</li>
                  <li>• 2♦</li>
                  <li>• 2♠</li>
                  <li>• A♠</li>
                  <li>• Books</li>
                  <li>• Bags</li>
                  <li>• Blinds</li>
                  <li className="col-span-2">• Ace Check</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* ====================================================== FOUNDING PLAYER */}
      <section style={{ background: '#141414' }} className="py-16 sm:py-24 border-y" data-testid="founding-player-section">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 text-center">
          <div className="text-xs sm:text-sm font-bold tracking-[0.3em] uppercase text-white/60">
            ★ ★ &nbsp; BECOME A &nbsp; ★ ★
          </div>
          <h2 className="spades-section-headline mt-3">
            <span style={{ color: '#00C96A' }}>FOUNDING</span>{' '}
            <span style={{ color: '#E63946' }}>PLAYER</span>
          </h2>
          <p className="mt-5 italic text-base sm:text-lg text-white/80 max-w-2xl mx-auto">
            Be one of the first <span className="font-bold not-italic text-white">1,000 players</span>{' '}
            to experience GEMS Live — the platform built for Spades players, by a Spades player.
          </p>

          <div
            className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full border"
            style={{ borderColor: '#FFD23F', background: 'rgba(255,210,63,0.08)' }}
            data-testid="founding-player-badge"
          >
            <Crown className="w-4 h-4" style={{ color: '#FFD23F' }} />
            <span className="text-xs sm:text-sm font-extrabold tracking-[0.2em] uppercase" style={{ color: '#FFD23F' }}>
              Founding Player Access
            </span>
          </div>

          <button
            type="button"
            onClick={() => scrollToForm('founding')}
            data-testid="founding-cta-button"
            className="mt-8 text-sm sm:text-base font-extrabold uppercase tracking-wide px-6 py-3 rounded-md"
            style={{ background: '#00C96A', color: '#0A0A0A' }}
          >
            Claim My Spot
          </button>
        </div>
      </section>

      {/* =================================================================== BENEFITS */}
      <section style={{ background: '#0A0A0A' }} className="py-16 sm:py-24" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 sm:gap-5">
            <Benefit color="#00C96A" Icon={Lock} title="Early Access" copy="Get in before the public launch." />
            <Benefit color="#E63946" Icon={Trophy} title="Tournament Priority" copy="First access to launch tournaments and special events." />
            <Benefit color="#00C96A" Icon={Gem} title="Monetization Position" copy="Be ready when creator and player earning opportunities open." />
            <Benefit color="#E63946" Icon={Users} title="Have a Voice" copy="Help shape the platform and the games we build." />
            <Benefit color="#FFD23F" Icon={Crown} title="Founding Player Status" copy="Exclusive badge on your profile when we go live." />
            <Benefit color="#E63946" Icon={Gift} title="Exclusive Perks" copy="Special rewards, drops, and more for early players." />
          </div>
        </div>
      </section>

      {/* ================================================================ SIGNUP FORM */}
      <section
        ref={formRef}
        id="signup"
        className="relative py-16 sm:py-24"
        style={{
          background:
            'radial-gradient(700px 400px at 90% 10%, rgba(230,57,70,0.15), transparent 60%), radial-gradient(700px 400px at 10% 90%, rgba(0,201,106,0.18), transparent 60%), #0A0A0A',
        }}
        data-testid="signup-section"
      >
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <h2 className="spades-section-headline text-center">
            <span className="text-white">CLAIM YOUR </span>
            <span style={{ color: '#E63946' }}>FOUNDING PLAYER</span>
            <span className="text-white"> SPOT</span>
          </h2>

          {/* 3-step indicator */}
          <ol className="mt-8 sm:mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-3">
            <Step n="1" color="#00C96A" title="SCAN THE QR CODE" sub="or visit the link below" />
            <Step n="2" color="#E63946" title="SIGN UP IN SECONDS" sub="Name, email, phone, and your city" />
            <Step n="3" color="#00C96A" title="GET LAUNCH DETAILS" sub="Be first to know about launch, events & more" />
          </ol>

          {/* ====================== QR + Printable-flyer downloads ====================== */}
          {/* May 23 (Spadeslaunch Bug 2): the page previously had a text-only
              "SCAN THE QR CODE" step but no actual QR on the page, and no way to
              download the printable flyer. This block shows the QR for visitors
              who arrived via the URL (no flyer in hand) and gives event
              organisers a one-click way to grab the printable flyer + the raw
              SVG of the QR for their own designs. */}
          <div
            className="mt-8 sm:mt-10 grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-6 sm:gap-8 items-center rounded-2xl border p-5 sm:p-7"
            style={{ borderColor: '#2A2A2A', background: 'rgba(20,20,20,0.6)' }}
            data-testid="spadeslaunch-qr-block"
          >
            <a
              href={ASSETS.qrSvg}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open QR code in new tab — scan to sign up"
              className="block mx-auto sm:mx-0 rounded-lg overflow-hidden bg-white p-3 sm:p-4 transition-transform hover:scale-[1.02]"
              style={{
                width: 'clamp(180px, 50vw, 240px)',
                boxShadow: '0 10px 30px -10px rgba(0,201,106,0.45)',
              }}
            >
              <img
                src={ASSETS.qrSolid}
                alt="Scan to claim your GEMS Live Founding Player spot"
                className="w-full h-auto block"
                width="220"
                height="220"
                loading="lazy"
                data-testid="spadeslaunch-qr-image"
              />
            </a>

            <div className="text-center sm:text-left">
              <div className="text-xs sm:text-sm font-bold uppercase tracking-[0.25em]" style={{ color: '#00C96A' }}>
                Scan or download
              </div>
              <h3 className="mt-2 text-xl sm:text-2xl font-extrabold leading-tight">
                Take GEMS Live with you.
              </h3>
              <p className="mt-2 text-sm sm:text-base text-white/70 max-w-md">
                Point your phone camera at the code, or grab the printable flyer for
                your next tournament or cookout.
              </p>

              <div className="mt-4 flex flex-col sm:flex-row flex-wrap gap-3">
                <a
                  href={ASSETS.flyerPrintable}
                  download="GEMS-Live-Spades-Flyer.png"
                  onClick={() => track('spadeslaunch_flyer_download', { qr_source: qrSource })}
                  data-testid="spadeslaunch-flyer-download"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md text-sm font-extrabold uppercase tracking-wide transition-transform active:scale-95"
                  style={{
                    background: '#00C96A',
                    color: '#0A0A0A',
                    boxShadow: '0 10px 20px -10px rgba(0,201,106,0.6)',
                  }}
                >
                  Download Printable Flyer
                </a>
                <a
                  href={ASSETS.qrSvg}
                  download="GEMS-Live-Spades-QR.svg"
                  onClick={() => track('spadeslaunch_qr_download', { qr_source: qrSource })}
                  data-testid="spadeslaunch-qr-download"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md text-sm font-extrabold uppercase tracking-wide border transition-transform active:scale-95"
                  style={{
                    background: '#141414',
                    color: '#FFFFFF',
                    borderColor: '#E63946',
                  }}
                >
                  Download QR Code Only
                </a>
              </div>
            </div>
          </div>
          {/* ============================================================================ */}

          {!submitted ? (
            <form
              onSubmit={handleSubmit}
              className="mt-10 sm:mt-12 rounded-2xl p-5 sm:p-8 border"
              style={{ borderColor: '#2A2A2A', background: 'rgba(20,20,20,0.7)', backdropFilter: 'blur(6px)' }}
              data-testid="spadeslaunch-form"
              noValidate
            >
              <Field
                label="Full Name *"
                value={form.full_name}
                onChange={handleChange('full_name')}
                onFocus={handleFocus}
                placeholder="Your name"
                error={errors.full_name}
                autoComplete="name"
                disabled={submitting}
                testid="form-full-name"
              />
              <Field
                label="Email Address *"
                type="email"
                value={form.email}
                onChange={handleChange('email')}
                onFocus={handleFocus}
                placeholder="you@example.com"
                error={errors.email}
                autoComplete="email"
                inputMode="email"
                disabled={submitting}
                testid="form-email"
              />
              <Field
                label="Phone Number *"
                type="tel"
                value={form.phone}
                onChange={handleChange('phone')}
                onFocus={handleFocus}
                placeholder="(404) 555-1234"
                error={errors.phone}
                autoComplete="tel"
                inputMode="tel"
                disabled={submitting}
                testid="form-phone"
              />
              <Field
                label="City, State (optional)"
                value={form.city_state}
                onChange={handleChange('city_state')}
                onFocus={handleFocus}
                placeholder="Atlanta, GA"
                autoComplete="address-level2"
                disabled={submitting}
                testid="form-city-state"
              />

              <div className="mb-4">
                <label className="block text-xs font-bold uppercase tracking-widest text-white/70 mb-2">
                  How did you hear about us? (optional)
                </label>
                <select
                  value={form.signup_source_detail}
                  onChange={handleChange('signup_source_detail')}
                  onFocus={handleFocus}
                  disabled={submitting}
                  className="w-full h-14 px-4 rounded-md text-base font-medium text-white outline-none transition border"
                  style={{
                    background: '#0A0A0A',
                    borderColor: '#2A2A2A',
                  }}
                  data-testid="form-heard"
                >
                  <option value="">— Select an option —</option>
                  {HEARD_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              </div>

              {errors._form && (
                <div className="mb-3 text-sm font-semibold" style={{ color: '#E63946' }} data-testid="form-error">
                  {errors._form}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting || !isFormValid}
                data-testid="form-submit"
                className="w-full mt-2 text-base sm:text-lg font-extrabold uppercase tracking-wide px-6 py-4 rounded-md transition-transform active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                style={{
                  background: 'linear-gradient(180deg, #00DD75 0%, #00B85F 100%)',
                  color: '#0A0A0A',
                  boxShadow: '0 10px 30px -10px rgba(0,201,106,0.6)',
                }}
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" /> Claiming…
                  </>
                ) : (
                  <>Claim My Founding Player Spot</>
                )}
              </button>

              <p className="mt-4 text-xs text-white/50 text-center">
                We never spam. Launch and tournament details only.
              </p>
            </form>
          ) : (
            <div
              className="mt-10 sm:mt-12 rounded-2xl p-7 sm:p-10 border text-center spades-fade-in"
              style={{ borderColor: '#00C96A', background: 'rgba(0,201,106,0.07)' }}
              data-testid="spadeslaunch-confirmation"
              role="status"
              aria-live="polite"
            >
              <CheckCircle2 className="w-14 h-14 mx-auto" style={{ color: '#00C96A' }} />
              <h3 className="mt-4 text-3xl sm:text-4xl font-black tracking-tight">
                {confirmation.already ? "YOU'RE ALREADY ON THE LIST." : "YOU'RE OFFICIALLY IN."}
              </h3>
              <p className="mt-3 text-base sm:text-lg text-white/80 max-w-xl mx-auto">
                {confirmation.already
                  ? "We've updated your info. Tournament details and your founding player access code will arrive at "
                  : 'Welcome to the founding circle. Tournament details, launch date, and your founding player access code will be sent to '}
                <span className="font-bold text-white">{confirmation.email}</span> before launch.
              </p>

              <div className="mt-7">
                <div className="text-xs font-bold uppercase tracking-widest text-white/60 mb-3">
                  Bring your Spades crew
                </div>
                <div className="flex flex-wrap justify-center gap-3">
                  <a
                    href={`sms:?&body=${encodeURIComponent(`${shareText} ${shareUrl}`)}`}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md font-bold text-sm"
                    style={{ background: '#E63946', color: '#FFFFFF' }}
                    data-testid="share-sms"
                  >
                    <MessageSquare className="w-4 h-4" /> SMS
                  </a>
                  <a
                    href={`https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`}
                    target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md font-bold text-sm"
                    style={{ background: '#00C96A', color: '#0A0A0A' }}
                    data-testid="share-whatsapp"
                  >
                    <MessageSquare className="w-4 h-4" /> WhatsApp
                  </a>
                  <button
                    type="button"
                    onClick={copyShareLink}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md font-bold text-sm border"
                    style={{ borderColor: '#2A2A2A', color: '#FFFFFF', background: '#141414' }}
                    data-testid="share-copy"
                  >
                    <Link2 className="w-4 h-4" />
                    {shareCopied ? 'Copied!' : 'Copy Link'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ============================================================ COOKOUT CULTURE */}
      <section style={{ background: '#141414' }} className="py-16 sm:py-24 border-t" data-testid="cookout-section">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <h2 className="spades-section-headline">
            <span className="block text-white">COOKOUT CULTURE.</span>
            <span className="block" style={{ color: '#00C96A' }}>COMPETITIVE SPIRIT.</span>
            <span className="block text-white">ONE NEW HOME.</span>
          </h2>
          <div className="mt-6 text-2xl sm:text-3xl font-extrabold tracking-tight">
            <span className="text-white/80">THIS IS SPADES. </span>
            <span style={{ color: '#E63946' }}>THIS IS GEMS LIVE.</span>
          </div>
          <div className="mt-8 flex justify-center text-5xl sm:text-6xl select-none" aria-hidden="true">
            <span className="spades-suit">♠</span>
            <span className="mx-3" style={{ color: '#E63946' }}>♥</span>
            <span style={{ color: '#E63946' }}>♦</span>
            <span className="ml-3 spades-suit">♣</span>
          </div>
        </div>
      </section>

      {/* ====================================================================== FOOTER */}
      {/* Mirrors the main MarketingLandingPage footer (May 14 update): brand
          column, Product / Legal / Support link groups, copyright bar. Visual
          + structural consistency so /spadeslaunch feels part of the same
          GEMS ecosystem (trust + investor perception). */}
      <footer className="border-t border-white/5 py-12 sm:py-16 px-4" style={{ background: '#0A0A0A' }} data-testid="spadeslaunch-footer">
        <div className="max-w-7xl mx-auto">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {/* Brand */}
            <div className="sm:col-span-2 lg:col-span-1">
              <div className="flex items-center gap-3 mb-4">
                <img
                  src={ASSETS.logo}
                  alt="GEMS Live"
                  className="w-10 h-10 object-contain"
                  loading="lazy"
                />
                <span className="text-lg font-bold">
                  GEMS <span className="text-emerald-400">Live</span>
                </span>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed">
                Game. Engage. Monetize. Stream.<br />
                The interactive social entertainment platform.
              </p>
            </div>

            {/* Product */}
            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-4 uppercase tracking-wider">Product</h4>
              <ul className="space-y-3">
                <li><a href="/beta-access" className="text-gray-500 hover:text-white text-sm transition-colors" data-testid="footer-web-app">Web App</a></li>
                <li><a href="https://apps.apple.com/app/gems-live/id6736983498" className="text-gray-500 hover:text-white text-sm transition-colors">iOS App</a></li>
                <li><a href="https://play.google.com/store/apps/details?id=com.gemslive.app" className="text-gray-500 hover:text-white text-sm transition-colors">Android App</a></li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-4 uppercase tracking-wider">Legal</h4>
              <ul className="space-y-3">
                <li><a href="/terms" className="text-gray-500 hover:text-white text-sm transition-colors" data-testid="footer-terms">Terms of Service</a></li>
                <li><a href="/privacy" className="text-gray-500 hover:text-white text-sm transition-colors" data-testid="footer-privacy">Privacy Policy</a></li>
                <li><a href="/community-guidelines" className="text-gray-500 hover:text-white text-sm transition-colors" data-testid="footer-community-guidelines">Community Guidelines</a></li>
              </ul>
            </div>

            {/* Support */}
            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-4 uppercase tracking-wider">Support</h4>
              <ul className="space-y-3">
                <li><a href="/support" className="text-gray-500 hover:text-white text-sm transition-colors" data-testid="footer-support">Help Center</a></li>
                <li><a href="mailto:support@gemslive.tv" className="text-gray-500 hover:text-white text-sm transition-colors" data-testid="footer-contact">Contact Us</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/5 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-gray-600 text-sm">© {new Date().getFullYear()} GEMS Live. All rights reserved.</p>
            
          </div>
        </div>
      </footer>

      {/* ============================================================ STICKY MOBILE CTA */}
      {stickyVisible && !submitted && (
        <button
          type="button"
          onClick={() => scrollToForm('sticky')}
          data-testid="sticky-cta"
          className="sm:hidden fixed bottom-3 left-3 right-3 z-50 text-base font-extrabold uppercase tracking-wide px-5 py-3.5 rounded-md spades-sticky-cta"
          style={{
            background: 'linear-gradient(180deg, #00DD75 0%, #00B85F 100%)',
            color: '#0A0A0A',
            boxShadow: '0 -8px 30px rgba(0,0,0,0.5), 0 10px 30px -8px rgba(0,201,106,0.55)',
            backdropFilter: 'blur(8px)',
          }}
        >
          Claim Your Founding Player Spot
        </button>
      )}
    </div>
  );
}

// =============================================================================
// Sub-components
// =============================================================================
const Bullet = ({ label }) => (
  <div className="flex items-center gap-3 text-base sm:text-lg">
    <span className="text-xl" aria-hidden="true">★</span>
    <span className="font-extrabold tracking-wide uppercase">{label}</span>
  </div>
);

const Benefit = ({ color, Icon, title, copy }) => (
  <div
    className="rounded-xl p-5 border-2 transition-transform hover:-translate-y-0.5"
    style={{ borderColor: color, background: '#141414' }}
    data-testid={`benefit-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
  >
    <Icon className="w-7 h-7" style={{ color }} />
    <div className="mt-3 text-base sm:text-lg font-extrabold uppercase tracking-wide">{title}</div>
    <div className="mt-1.5 text-sm text-white/70 leading-snug">{copy}</div>
  </div>
);

const Step = ({ n, color, title, sub }) => (
  <li className="flex sm:flex-col items-start sm:items-center gap-3 sm:gap-2 sm:text-center">
    <div
      className="w-10 h-10 rounded-full flex items-center justify-center text-base font-black shrink-0"
      style={{ background: color, color: '#0A0A0A' }}
    >
      {n}
    </div>
    <div>
      <div className="text-sm font-extrabold uppercase tracking-wide">{title}</div>
      <div className="text-xs text-white/60 italic">{sub}</div>
    </div>
  </li>
);

const Field = ({
  label, value, onChange, onFocus, placeholder, type = 'text', error,
  autoComplete, inputMode, disabled, testid,
}) => (
  <div className="mb-4">
    <label className="block text-xs font-bold uppercase tracking-widest text-white/70 mb-2">{label}</label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      onFocus={onFocus}
      placeholder={placeholder}
      autoComplete={autoComplete}
      inputMode={inputMode}
      disabled={disabled}
      data-testid={testid}
      className="w-full h-14 px-4 rounded-md text-base font-medium text-white outline-none transition border focus:outline-none"
      style={{
        background: '#0A0A0A',
        borderColor: error ? '#E63946' : '#2A2A2A',
        boxShadow: error ? 'inset 0 0 0 1px #E63946' : 'none',
      }}
    />
    {error && (
      <div className="mt-1.5 text-xs font-semibold" style={{ color: '#E63946' }} data-testid={`${testid}-error`}>
        {error}
      </div>
    )}
  </div>
);

// =============================================================================
// Global page styles (scoped via class names — never globals from React tree)
// =============================================================================
const SpadesGlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Anton&family=Manrope:wght@400;500;700;800;900&display=swap');

    .spades-hero-headline {
      font-family: 'Anton', 'Bebas Neue', system-ui, sans-serif;
      font-weight: 900;
      line-height: 0.92;
      letter-spacing: 0.005em;
      font-size: clamp(48px, 13vw, 128px);
      text-shadow:
        0 4px 0 rgba(0,0,0,0.45),
        0 0 28px rgba(0,0,0,0.7);
      transform: skew(-2deg);
      transform-origin: left center;
      word-spacing: 0.05em;
    }

    .spades-section-headline {
      font-family: 'Anton', 'Bebas Neue', system-ui, sans-serif;
      font-weight: 900;
      line-height: 1.0;
      letter-spacing: 0.01em;
      font-size: clamp(34px, 8vw, 80px);
      text-shadow: 0 3px 0 rgba(0,0,0,0.4);
    }

    /* Extra-small mobile (≤360px): tame hero/section type so it doesn't overflow. */
    @media (max-width: 380px) {
      .spades-hero-headline { font-size: 48px; line-height: 0.9; }
      .spades-section-headline { font-size: 32px; }
    }

    .spades-suit {
      display: inline-block;
      animation: spadesSuitBounce 1.6s ease-out 0.2s 1 both;
    }
    .spades-suit-2 { animation-delay: 0.32s; }
    .spades-suit-3 { animation-delay: 0.44s; }
    .spades-suit-4 { animation-delay: 0.56s; }

    @keyframes spadesSuitBounce {
      0%   { transform: translateY(-12px); opacity: 0; }
      55%  { transform: translateY(4px);  opacity: 1; }
      80%  { transform: translateY(-2px); }
      100% { transform: translateY(0);    opacity: 1; }
    }

    .spades-pulse {
      width: 7px; height: 7px; border-radius: 9999px;
      background: #FFFFFF;
      box-shadow: 0 0 0 0 rgba(255,255,255,0.7);
      animation: spadesPulse 1.6s infinite;
    }
    @keyframes spadesPulse {
      0%   { box-shadow: 0 0 0 0 rgba(255,255,255,0.7); }
      70%  { box-shadow: 0 0 0 10px rgba(255,255,255,0); }
      100% { box-shadow: 0 0 0 0 rgba(255,255,255,0); }
    }

    .spades-fade-in { animation: spadesFadeIn 360ms ease-out both; }
    @keyframes spadesFadeIn {
      from { opacity: 0; transform: translateY(8px); }
      to   { opacity: 1; transform: translateY(0); }
    }

    .spades-sticky-cta {
      animation: spadesStickyIn 220ms ease-out both;
    }
    @keyframes spadesStickyIn {
      from { transform: translateY(12px); opacity: 0; }
      to   { transform: translateY(0);    opacity: 1; }
    }

    /* Form input focus state — emerald glow. */
    [data-testid^="form-"]:focus,
    [data-testid^="form-"] input:focus,
    [data-testid="form-heard"]:focus {
      border-color: #00C96A !important;
      box-shadow: 0 0 0 3px rgba(0,201,106,0.22) !important;
    }

    /* Respect reduced-motion users. */
    @media (prefers-reduced-motion: reduce) {
      .spades-suit, .spades-pulse, .spades-fade-in, .spades-sticky-cta {
        animation: none !important;
      }
    }
  `}</style>
);
